// src/pages/Campaigns/SurveyGenie/QuestionBank.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, CardActions,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Alert, Snackbar, CircularProgress,
    List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction,
    Divider, InputAdornment, Tooltip, FormControl, InputLabel, Select,
    MenuItem, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
    ArrowBack, Add, Delete, Edit, Search, FilterList, ContentCopy,
    ExpandMore, RadioButtonChecked, CheckBox, TextFields, StarRate,
    LinearScale, ArrowDropDownCircle, FormatListNumbered, GridOn,
    DateRange, AttachFile, Speed, Email, Phone, Numbers, Tune,
    ToggleOn, Category, Visibility, Download, Upload
} from '@mui/icons-material';
import {
    QUESTION_TYPE, QUESTION_TYPE_LABELS, QUESTION_BANK_CATEGORY,
    QUESTION_BANK_CATEGORY_LABELS, hasOptions, getDefaultSettings
} from './surveyEnums';
import axiosInstance from '../../../utils/axiosInstance'; 


const QuestionBank = () => {
    const navigate = useNavigate();
    const { chatbotId } = useParams();
    
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Dialog states
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [importDialog, setImportDialog] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [selectedSurvey, setSelectedSurvey] = useState('');
    
    // Form state
    const [questionForm, setQuestionForm] = useState({
        question_text: '',
        question_type: QUESTION_TYPE.SINGLE_CHOICE,
        category: QUESTION_BANK_CATEGORY.GENERAL,
        description: '',
        is_required: false,
        options: [{ text: '', value: '' }],
        settings: {}
    });

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: page + 1,
                per_page: rowsPerPage
            };
            if (searchTerm) params.search = searchTerm;
            if (categoryFilter) params.category = categoryFilter;
            if (typeFilter) params.question_type = typeFilter;
            
            // Use axiosInstance
            const response = await axiosInstance.get('/api/question-bank', { params });
            if (response.data.success) {
                // Backend returns { data: [], pagination: { total: ... } }
                setQuestions(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            showSnackbar('Error loading question bank', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchTerm, categoryFilter, typeFilter]);

    const fetchSurveys = async () => {
        try {
            const response = await axiosInstance.get('/api/surveys', { params: { per_page: 100 } });
            if (response.data.success) {
                setSurveys(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching surveys:', error);
        }
    };    

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const getQuestionTypeIcon = (type) => {
        const iconMap = {
            [QUESTION_TYPE.SINGLE_CHOICE]: <RadioButtonChecked />,
            [QUESTION_TYPE.MULTIPLE_CHOICE]: <CheckBox />,
            [QUESTION_TYPE.TEXT_SHORT]: <TextFields />,
            [QUESTION_TYPE.TEXT_LONG]: <TextFields />,
            [QUESTION_TYPE.RATING_SCALE]: <StarRate />,
            [QUESTION_TYPE.LIKERT_SCALE]: <LinearScale />,
            [QUESTION_TYPE.DROPDOWN]: <ArrowDropDownCircle />,
            [QUESTION_TYPE.RANKING]: <FormatListNumbered />,
            [QUESTION_TYPE.MATRIX]: <GridOn />,
            [QUESTION_TYPE.DATE]: <DateRange />,
            [QUESTION_TYPE.FILE_UPLOAD]: <AttachFile />,
            [QUESTION_TYPE.NET_PROMOTER_SCORE]: <Speed />,
            [QUESTION_TYPE.EMAIL]: <Email />,
            [QUESTION_TYPE.PHONE]: <Phone />,
            [QUESTION_TYPE.NUMBER]: <Numbers />,
            [QUESTION_TYPE.SLIDER]: <Tune />,
            [QUESTION_TYPE.YES_NO]: <ToggleOn />
        };
        return iconMap[type] || <TextFields />;
    };

    const resetForm = () => {
        setQuestionForm({
            question_text: '',
            question_type: QUESTION_TYPE.SINGLE_CHOICE,
            category: QUESTION_BANK_CATEGORY.GENERAL,
            description: '',
            is_required: false,
            options: [{ text: '', value: '' }],
            settings: {}
        });
    };

    const handleCreateQuestion = async () => {
        if (!questionForm.question_text.trim()) {
            showSnackbar('Question text is required', 'error');
            return;
        }
        
        try {
            const payload = {
                ...questionForm,
                options: hasOptions(questionForm.question_type) 
                    ? questionForm.options.filter(o => o.text.trim())
                    : []
            };
            
            const response = await axiosInstance.post('/api/question-bank', payload);
            
            if (response.data.success) {
                showSnackbar('Question added to bank');
                setCreateDialog(false);
                resetForm();
                fetchQuestions();
            }
        } catch (error) {
            console.error('Error creating question:', error);
            showSnackbar(error.response?.data?.message || 'Error creating question', 'error');
        }
    };

    const handleUpdateQuestion = async () => {
        if (!questionForm.question_text.trim()) {
            showSnackbar('Question text is required', 'error');
            return;
        }
        
        try {
            const payload = {
                ...questionForm,
                options: hasOptions(questionForm.question_type) 
                    ? questionForm.options.filter(o => o.text.trim())
                    : []
            };
            
            const response = await axiosInstance.put(`/api/question-bank/${selectedQuestion.id}`, payload);
            
            if (response.data.success) {
                showSnackbar('Question updated');
                setEditDialog(false);
                resetForm();
                fetchQuestions();
            }
        } catch (error) {
            console.error('Error updating question:', error);
            showSnackbar(error.response?.data?.message || 'Error updating question', 'error');
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return;
        
        try {
            const response = await axiosInstance.delete(`/api/question-bank/${questionId}`);
            
            if (response.data.success) {
                showSnackbar('Question deleted');
                fetchQuestions();
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            showSnackbar(error.response?.data?.message || 'Error deleting question', 'error');
        }
    };

    const handleImportToSurvey = async () => {
        if (!selectedSurvey || !selectedQuestion) {
            showSnackbar('Please select a survey', 'error');
            return;
        }
        
        try {
            const response = await axiosInstance.post('/api/question-bank/import', {
                survey_id: selectedSurvey,
                bank_question_id: selectedQuestion.id
            });
            
            if (response.data.success) {
                showSnackbar('Question imported to survey');
                setImportDialog(false);
                setSelectedSurvey('');
            }
        } catch (error) {
            console.error('Error importing question:', error);
            showSnackbar(error.response?.data?.message || 'Error importing question', 'error');
        }
    };

    const openEditDialog = (question) => {
        setSelectedQuestion(question);
        setQuestionForm({
            question_text: question.question_text,
            question_type: question.question_type,
            category: question.category,
            description: question.description || '',
            is_required: question.is_required,
            options: question.options?.length > 0 
                ? question.options 
                : [{ text: '', value: '' }],
            settings: question.settings || {}
        });
        setEditDialog(true);
    };

    const openImportDialog = (question) => {
        setSelectedQuestion(question);
        fetchSurveys();
        setImportDialog(true);
    };

    const addOption = () => {
        setQuestionForm({
            ...questionForm,
            options: [...questionForm.options, { text: '', value: '' }]
        });
    };

    const updateOption = (index, field, value) => {
        const newOptions = [...questionForm.options];
        newOptions[index][field] = value;
        if (field === 'text' && !newOptions[index].value) {
            newOptions[index].value = value.toLowerCase().replace(/\s+/g, '_');
        }
        setQuestionForm({ ...questionForm, options: newOptions });
    };

    const removeOption = (index) => {
        const newOptions = questionForm.options.filter((_, i) => i !== index);
        setQuestionForm({ ...questionForm, options: newOptions });
    };

    const renderQuestionForm = () => (
        <>
            <TextField
                fullWidth
                label="Question Text"
                value={questionForm.question_text}
                onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                required
                margin="normal"
                multiline
                rows={2}
            />
            
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Question Type</InputLabel>
                        <Select
                            value={questionForm.question_type}
                            label="Question Type"
                            onChange={(e) => setQuestionForm({ 
                                ...questionForm, 
                                question_type: e.target.value,
                                settings: getDefaultSettings(e.target.value)
                            })}
                        >
                            {Object.entries(QUESTION_TYPE_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                    <Box display="flex" alignItems="center">
                                        {getQuestionTypeIcon(key)}
                                        <Typography sx={{ ml: 1 }}>{label}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={questionForm.category}
                            label="Category"
                            onChange={(e) => setQuestionForm({ ...questionForm, category: e.target.value })}
                        >
                            {Object.entries(QUESTION_BANK_CATEGORY_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            
            <TextField
                fullWidth
                label="Description / Help Text"
                value={questionForm.description}
                onChange={(e) => setQuestionForm({ ...questionForm, description: e.target.value })}
                margin="normal"
                multiline
                rows={2}
            />
            
            {hasOptions(questionForm.question_type) && (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Answer Options
                    </Typography>
                    {questionForm.options.map((option, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                            <TextField
                                fullWidth
                                size="small"
                                label={`Option ${index + 1}`}
                                value={option.text}
                                onChange={(e) => updateOption(index, 'text', e.target.value)}
                            />
                            <TextField
                                size="small"
                                label="Value"
                                value={option.value}
                                onChange={(e) => updateOption(index, 'value', e.target.value)}
                                sx={{ width: 150 }}
                            />
                            <IconButton 
                                size="small" 
                                onClick={() => removeOption(index)}
                                disabled={questionForm.options.length <= 1}
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                    <Button startIcon={<Add />} onClick={addOption} size="small">
                        Add Option
                    </Button>
                </Box>
            )}
        </>
    );

    if (loading && questions.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            Question Bank
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Reusable questions for your surveys
                        </Typography>
                    </Box>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<Add />}
                    onClick={() => {
                        resetForm();
                        setCreateDialog(true);
                    }}
                >
                    Add Question
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search questions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Category"
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {Object.entries(QUESTION_BANK_CATEGORY_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Question Type</InputLabel>
                            <Select
                                value={typeFilter}
                                label="Question Type"
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {Object.entries(QUESTION_TYPE_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                            {totalCount} questions
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Questions List */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Question</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Used</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {questions.map((question) => (
                                <TableRow key={question.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {question.question_text}
                                        </Typography>
                                        {question.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {question.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getQuestionTypeIcon(question.question_type)}
                                            label={QUESTION_TYPE_LABELS[question.question_type]}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<Category />}
                                            label={QUESTION_BANK_CATEGORY_LABELS[question.category]}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {question.usage_count || 0} times
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Preview">
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    setSelectedQuestion(question);
                                                    setPreviewDialog(true);
                                                }}
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Import to Survey">
                                            <IconButton 
                                                size="small"
                                                onClick={() => openImportDialog(question)}
                                            >
                                                <Upload />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton 
                                                size="small"
                                                onClick={() => openEditDialog(question)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton 
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteQuestion(question.id)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {questions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography color="text.secondary" py={4}>
                                            No questions found. Add some questions to build your library.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* Create Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add Question to Bank</DialogTitle>
                <DialogContent>
                    {renderQuestionForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateQuestion}>
                        Add Question
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Question</DialogTitle>
                <DialogContent>
                    {renderQuestionForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateQuestion}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Question Preview</DialogTitle>
                <DialogContent>
                    {selectedQuestion && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {selectedQuestion.question_text}
                            </Typography>
                            {selectedQuestion.description && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {selectedQuestion.description}
                                </Typography>
                            )}
                            <Box mt={2}>
                                <Chip
                                    icon={getQuestionTypeIcon(selectedQuestion.question_type)}
                                    label={QUESTION_TYPE_LABELS[selectedQuestion.question_type]}
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={<Category />}
                                    label={QUESTION_BANK_CATEGORY_LABELS[selectedQuestion.category]}
                                />
                            </Box>
                            {selectedQuestion.options?.length > 0 && (
                                <Box mt={3}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Options:
                                    </Typography>
                                    <List dense>
                                        {selectedQuestion.options.map((option, idx) => (
                                            <ListItem key={idx}>
                                                <ListItemIcon>
                                                    {selectedQuestion.question_type === QUESTION_TYPE.SINGLE_CHOICE 
                                                        ? <RadioButtonChecked fontSize="small" />
                                                        : <CheckBox fontSize="small" />
                                                    }
                                                </ListItemIcon>
                                                <ListItemText primary={option.text} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        onClick={() => {
                            setPreviewDialog(false);
                            openImportDialog(selectedQuestion);
                        }}
                    >
                        Import to Survey
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Import Question to Survey</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select a survey to import this question:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" mb={2}>
                        "{selectedQuestion?.question_text}"
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Select Survey</InputLabel>
                        <Select
                            value={selectedSurvey}
                            label="Select Survey"
                            onChange={(e) => setSelectedSurvey(e.target.value)}
                        >
                            {surveys.map((survey) => (
                                <MenuItem key={survey.id} value={survey.id}>
                                    {survey.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImportDialog(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleImportToSurvey}
                        disabled={!selectedSurvey}
                    >
                        Import
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default QuestionBank;